/**
 * PackLandingTemplate — the Toile pack page.
 *
 * Copied from `@product/narcis/packs/PackLandingTemplate.tsx`, first cut to a showcase-only shell
 * (plan 2026_08_30_toile_pack_page_convergence), converged onto the narcis/ovid canon SHAPE per
 * operator instruction 2026-09-01, then rebuilt on `core/packs/PackPageShell` (plan
 * 2026_09_01_pack_page_shell.md, step 3) — the shell IS that canon shape now, factored once and
 * shared. Narcis's/ovid's own pack-page component is the funnel's control room: the pack machine,
 * the four hero states A/B/C/D and their owner sub-states, the queue, the consent and auth gates,
 * entitlement, the model chip, the step strip, the returning-buyer banner, the reset dialog, the
 * polling fetchers. Toile has no account, no generation and nothing to sell, so none of that state
 * machinery exists here — this file only builds the slot content the shell renders: the mobile
 * header hoist, the sticky left aside (eyebrow, H1, tagline, then the page verb — no price row and
 * no step strip: the shell conditions both on props this file never passes), the stage on the
 * right, the related-packs strip, the gallery-proof rows below, the SEO backbone (intro/use-cases,
 * FAQ, and the always-rendered About section), the footer, and the sign-in card as the one overlay.
 *
 * The page verb arrived with wall plan e (`thinking/plans/2026_08_31_wall_plan_e_toile_page_to_
 * wall.md`). It is the page's one action and it is one-shot: it writes nine queued rows and hands
 * the visitor to the wall, which is where anything they make lives (BRIEF §9). Nothing about a
 * generation is rendered here; the page holds no session. Since plan 2026_09_23_pack_stage_coda.md
 * the verb (`StageComponent`) lives in the stage's coda, the frame after the last look, at every
 * width; this file creates it ONCE and hands it to the stage, and the shell's `asideAfterTagline`
 * slot carries the coda door that pages there.
 *
 * The per-cell "recipe" — subject/style/palette/framing rendered as a visible text block in the
 * aside, plus a repeated visible chip row under every grid cell — is GONE: narcis and ovid carry no
 * such block anywhere on their own pack pages, and the operator named it directly as the drift the
 * 2026-09-01 convergence removed. The four handles it carried are not lost — they travel as
 * descriptive `<img alt>` text on every sample (`PackGrid.tsx`, `PackHeroStage.tsx`) and as each
 * image's `caption` in the route's `ImageGallery` JSON-LD, exactly the register canon's own
 * galleryProof cells use (a bare image, no visible per-cell caption).
 *
 * The DB prompts (`images.pack_prompts`, product `toile`) are NOT rendered here. The
 * `general_generator` head is explicit: the prompt is the asset, it lives in Postgres, is fetched
 * per generation, and never renders.
 */

import {useEffect, useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router'

import {PackPageShell, type PackPageSeoSection} from '@core/packs/PackPageShell'
import {GATE_TRIGGER_PAGE_VERB} from '@core/wall/contract'
import {basisWallPath, readPackReturnToken} from '@core/wall/pageVerb'
import {track, type FunnelEventMap} from '@core/utils/analytics'
import {codaIndex} from '@core/packs/stage/pager'
import {cellsTravel as tileIdsTravel} from '@core/packs/stage/travel'
import {CodaDoor} from '@core/packs/stage/CodaDoor'
import {Header} from '@product/toile/components/Header/Header'
import {Footer} from '@product/toile/components/Footer/Footer'
// The wall's own gate, now a component both surfaces open (plan e: "the sign-in card and
// return-token mechanism from plan d" — reuse it, do not build a second).
import {SignInLayer} from '@core/auth-ui/SignInLayer/SignInLayer'
import {TOILE_SIGN_IN_CARD} from '@product/toile/wall/signInCard'
import {StageComponent} from './StageComponent'
import {packCells, type ToilePack} from './data'
import {PackHeroStage, type LookMethod} from './PackHeroStage'
import {FAQ_OWN_VERSION, codaDoorLabel} from './copy'
import {PackGrid} from './PackGrid'
import {PackGalleryCaption} from './PackGalleryCaption'
import {PackRelatedStrip, type PackRelatedCard} from './PackRelatedStrip'
import {ABOUT_TOILE} from './aboutToile'
import styles from './PackLandingTemplate.module.scss'

/**
 * The pack page's FAQ, in one place.
 *
 * Rendered below AND emitted as the route's `FAQPage` JSON-LD — a FAQ block whose answers are not
 * on the page is a schema Google discards, so the two must come from one array rather than two
 * hand-kept copies. `pack.seo.faq` overrides the two generic entries when present (Track A item 4,
 * plan `thinking/plans/2026_08_31_toile_pack_wiring.md`) — the route imports this same function for
 * its JSON-LD, so the override reaches both places without touching the route file.
 */
export function packFaq(pack: ToilePack): {q: string; a: string}[] {
    if (pack.seo?.faq?.length) return pack.seo.faq
    return [
        {
            q: `What is the ${pack.name.toLowerCase()} pack?`,
            a: `${pack.tagline} Each sample has its own subject, style, palette and framing.`,
        },
        // Rewritten with the page each time the verb moves (wall plan e, then the coda plan
        // 2026_09_23): it is also the page's FAQPage JSON-LD, so a stale answer is a stale answer
        // in the index.
        FAQ_OWN_VERSION,
    ]
}

interface PackLandingTemplateProps {
    pack: ToilePack
    /** State-A related-strip heading — Toile has no per-pack authored override yet, so every pack
     *  reads the shared fallback ('More like this', `relatedPacks.ts`'s `DEFAULT_RELATED_TITLE`). */
    relatedTitle?: string
    /** Up to 4 related-pack cards, resolved server-side (`relatedPacks.ts` against `catalog.ts`'s
     *  `getCatalogCards()`) and passed in fully built — this component is presentation only. */
    relatedPacksCards?: PackRelatedCard[]
    /** The route's three JSON-LD schemas (breadcrumb/FAQ/image-gallery), passed through to the
     *  shell (plan §6 Q3) — same builders, same order, same position in the document stream as
     *  before this pass (the route used to render these `<script>` tags itself, immediately
     *  before this component; the shell now renders them in that same position). */
    jsonLd?: object[]
    /** Wall plan e — whether the visitor can be written for. Anonymous, the verb opens the sign-in
     *  card instead of writing rows (BRIEF §10). Defaulted so a caller that predates the verb (the
     *  SSR spec) still renders the page, as an anonymous visitor's. */
    signedIn?: boolean
    /** The nine cells as the wall knows them, by position — `null` where the wall holds no tile.
     *  Wall plan e: a cell tapped here is a quick look that lands armed on the wall (BRIEF §9). */
    tileIdsByPosition?: (number | null)[]
}

export function PackLandingTemplate({
    pack, relatedTitle = 'More like this', relatedPacksCards = [], jsonLd, signedIn = false,
    tileIdsByPosition = [],
}: PackLandingTemplateProps) {
    const [searchParams] = useSearchParams()
    const coda = codaIndex(packCells(pack).length)
    // A visitor coming back from the sign-in card with a press in the URL lands on the coda: the
    // re-submit, the terms card or its one-line refusal all render there.
    const [selected, setSelected] = useState(() => (readPackReturnToken(searchParams, pack.slug) ? coda : 0))
    /** Bumped by the coda door; `StageComponent` focuses its field on each new value. */
    const [focusToken, setFocusToken] = useState(0)
    /** The URL the sign-in card must return to, carrying the press (`pageVerb.packReturnToken`) —
     *  and, being non-null, the fact that the card is open at all. */
    const [gateRedirect, setGateRedirect] = useState<string | null>(null)
    const navigate = useNavigate()

    /** A pack the wall knows every cell of — the grid and the look door read the same predicate. */
    const cellsTravel = tileIdsTravel(tileIdsByPosition)

    /** Every move of the stage — zone, swipe, key, thumb, and the coda door — lands here. */
    function selectLook(idx: number, method: LookMethod) {
        setSelected(idx)
        // `pack_look_advanced` lives on `FunnelEventMap`, while `track()`'s generic is pinned to
        // `CoreEventMap` — the `as any` below is the seam between the two, the same one
        // `@core/products/funnelEvents.ts` crosses.
        const params: FunnelEventMap['pack_look_advanced'] = {
            pack_slug: pack.slug,
            method,
            look_index: idx,
            // Required by the type, constant here: the Toile stage only ever renders the showcase.
            visual_stage: 'ad',
        }
        track('pack_look_advanced' as any, params as any)
    }

    function openCoda() {
        selectLook(coda, 'door')
        setFocusToken((t) => t + 1)
    }

    // After the coda door's render: the coda is visible, its field focused (the child's effect runs
    // first); bring the stage into view, minimally, so a phone reader sees the frame they opened.
    useEffect(() => {
        if (focusToken === 0) return
        document.getElementById('stage')?.scrollIntoView({block: 'nearest', behavior: 'smooth'})
    }, [focusToken])

    function handleCell(index: number) {
        const tileId = cellsTravel ? tileIdsByPosition[index] : null
        if (tileId === null) {
            setSelected(index)
            return
        }
        navigate(basisWallPath(tileId))
    }
    const faq = packFaq(pack)
    const seo = pack.seo
    const h1 = seo?.h1 ?? pack.name
    const heroTagline = seo?.tagline ?? pack.tagline

    const eyebrow = `/${pack.slug} · ${pack.category}`

    // seoSections — intro+use-cases (iff `seo`) → FAQ (always) → About (always), exactly the
    // render this replaces (verified byte-identical by the SSR snapshot guard).
    const seoSectionCandidates: (PackPageSeoSection | false)[] = [
        !!seo && {
            key: 'intro',
            paragraphs: [seo.intro],
            bullets: seo.useCases.length > 0 ? seo.useCases : undefined,
        },
        {
            key: 'faq',
            title: 'Frequently asked questions',
            faq,
        },
        {
            key: 'about',
            title: ABOUT_TOILE.title,
            paragraphs: [ABOUT_TOILE.paragraph],
        },
    ]
    const seoSections = seoSectionCandidates.filter((s): s is PackPageSeoSection => !!s)

    return (
        <PackPageShell
            styles={styles}
            rootTag="main"
            jsonLd={jsonLd}
            header={<Header accent="packs" />}
            eyebrow={eyebrow}
            h1={h1}
            tagline={heroTagline}
            // The slot narcis/ovid fill with their CTA carries the way to it: the page's action
            // lives in the stage's coda (plan 2026_09_23 § Decided 4–5).
            asideAfterTagline={<CodaDoor label={codaDoorLabel(pack.name)} onOpen={openCoda} />}
            stage={
                <PackHeroStage
                    pack={pack}
                    selected={selected}
                    onSelect={selectLook}
                    tileIdsByPosition={tileIdsByPosition}
                    action={
                        // The one mount. Keyed on the pack: `PackRelatedStrip`'s links are client
                        // navigations within this same route module, so without a key React reuses
                        // the mounted instance across a pack→pack move and the field, `pendingRef`
                        // and a held `request_id` all survive into the new pack — a press there would
                        // carry the OLD pack's words and key (F1, wall plan e review).
                        <StageComponent
                            key={pack.slug}
                            pack={pack}
                            signedIn={signedIn}
                            onGate={setGateRedirect}
                            focusToken={focusToken}
                        />
                    }
                />
            }
            related={
                // Related strip — canon order (narcis/ovid): part of the persistent document,
                // between the hero and the gallery proof, not a hero-band-only element.
                <PackRelatedStrip
                    title={relatedTitle}
                    linkLabel="the whole catalog →"
                    linkHref="/packs"
                    cards={relatedPacksCards}
                />
            }
            galleryAriaLabel="Pack sample gallery"
            galleryCaption={<PackGalleryCaption />}
            galleryBody={<PackGrid pack={pack} onSelect={handleCell} travels={cellsTravel} />}
            seoSections={seoSections}
            footer={<Footer />}
            // The shell's own slot for a layer outside `.page` — the sign-in card is the one
            // overlay (BRIEF §12), so the shell needs nothing added for it.
            overlay={
                gateRedirect !== null ? (
                    <SignInLayer
                        redirectTo={gateRedirect}
                        trigger={GATE_TRIGGER_PAGE_VERB}
                        onClose={() => setGateRedirect(null)}
                        card={TOILE_SIGN_IN_CARD}
                    />
                ) : undefined
            }
        />
    )
}
